import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { RefreshCw, Lock } from "lucide-react";
import { toast } from "sonner";
import { MenuEditor } from "@/components/admin/MenuEditor";
import { QrCard } from "@/components/admin/MenuQrCard";
import { BrunchScheduleEditor } from "@/components/admin/BrunchScheduleEditor";

const TABS = [
  { id: "brunch", label: "Brunch (public)" },
  { id: "table", label: "Table QR Menu (hidden)" },
];

const TableCodeCard = () => {
  const [code, setCode] = useState("");
  const [draft, setDraft] = useState("");

  useEffect(() => {
    adminApi.get("/admin/menus/table-code").then(({ data }) => setCode(data.code));
  }, []);

  async function update(newCode) {
    const msg = "Old QR codes will stop working right away. You'll need to print the new one. Continue?";
    if (!window.confirm(msg)) return;
    try {
      const { data } = await adminApi.post("/admin/menus/table-code", { code: newCode || null });
      setCode(data.code);
      setDraft("");
      toast.success("New secret code set — print the new QR");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  if (!code) return null;
  return (
    <QrCard
      url={`${window.location.origin}/t/${code}`}
      label="Hidden Table Menu QR"
      title="Scan For The Table Menu"
      description="Only people who scan this code can see this menu. It's not linked anywhere on the public site and won't show in Google."
      printHeading="Scan For Our Table Menu"
      filename="trio-table-menu-qr.png"
      testid="table-qr-card"
    >
      <div className="mt-6 pt-4 border-t border-[var(--trio-border)]">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-bold mb-2">
          <Lock className="w-3 h-3" /> Secret code: <span data-testid="table-qr-current-code" className="font-mono normal-case tracking-normal">{code}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Custom code (optional)" data-testid="table-qr-code-input" className="rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)] max-w-[220px]" />
          <button onClick={() => update(draft.trim())} data-testid="table-qr-change-code" className="inline-flex items-center gap-2 border border-[var(--trio-border)] hover:border-[var(--trio-primary)] px-4 py-2 text-xs uppercase tracking-widest font-bold">
            <RefreshCw className="w-3.5 h-3.5" /> {draft.trim() ? "Set Code" : "Generate New Code"}
          </button>
        </div>
      </div>
    </QrCard>
  );
};

export default function MenusManager() {
  const [tab, setTab] = useState("brunch");
  return (
    <div data-testid="menus-manager" className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`menus-tab-${t.id}`}
            className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
              tab === t.id ? "bg-[var(--trio-text)] text-[var(--trio-bg)] border-[var(--trio-text)]" : "border-[var(--trio-border)] hover:border-[var(--trio-text)]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "brunch" ? (
        <>
          <p className="text-sm text-[var(--trio-muted)]">Shows as a "Brunch" tab on the public Menu once it has at least one item. It's also included at the top of the hidden table menu.</p>
          <MenuEditor
            menuId="brunch"
            noteLabel="Brunch days / hours (shown on menu)"
            notePlaceholder="Saturday & Sunday · 12pm – 4pm"
            renderExtra={(menu, setMenu) => <BrunchScheduleEditor menu={menu} setMenu={setMenu} />}
          />
        </>
      ) : (
        <>
          <TableCodeCard />
          <p className="text-sm text-[var(--trio-muted)]">Hookahs, drinks, and anything else you only want table guests to see. Brunch is shown automatically above these sections.</p>
          <MenuEditor menuId="table" noteLabel="Note at top (optional)" notePlaceholder="Must be 21+ for hookah" />
        </>
      )}
    </div>
  );
}
